package com.neurobs.react

import com.neurobs.ccab.CCABServer
import com.neurobs.errors.Result
import com.neurobs.js.ccab.ServerContext
import diode.{Action, Dispatcher}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.{useEffect, useState}
import slinky.core.facade.{React, ReactElement}
import slinky.web.html.{className, span}

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.Future

trait CardContentProvider {
	import CardContentProvider._

	def managed: Boolean = true
	def title: String
	def canClose: Boolean
	def refreshFunction: Option[CCABServer => Future[Result[AnyRef]]]
	def content(d: Delegate, contentData: Option[AnyRef]): ReactElement
	val key: String = {idCounter += 1; idCounter}.toString
}

object CardContentProvider {
	var idCounter = 0

	trait Delegate {
		def addCard(card: CardContentProvider, afterThis: Boolean)
		def refreshThis(): Unit
		def replaceWithError(error: String): Unit
		def closeThis(): Unit
	}
}

object CardDeck {

	case class Model
	(
		cards: Seq[CardContentProvider]
	)

	case class Remove(card: CardContentProvider) extends Action
	case class Add(card: CardContentProvider, after: Option[CardContentProvider]) extends Action

	class AppCircuit(val initialModel: Model) extends SimpleCircuit[Model] {
		override def handle(value: Model, action: Any): Option[Model] = action match {
			case Remove(card) =>
				value.cards.find(_ eq card).map(_ => Model(value.cards.filterNot(_ eq card)))
			case Add(card, after) =>
				val cards = value.cards
				if (cards.contains(card)) None
				else after match {
					case None => Some(Model(cards :+ card))
					case Some(a) =>
						cards.indexOf(a) match {
							case -1 => Some(Model(cards :+ card))
							case i => Some(Model(cards.take(i + 1) ++ Seq(card) ++ cards.drop(i + 1)))
						}
				}
		}
	}

	@react object CardElement {
		case class State
		(
			loading: Boolean = false,
			error: Option[String] = None,
			data: Option[AnyRef] = None
		)
		case class Props
		(
			card: CardContentProvider,
			dispatcher: Dispatcher
		)

		val component = React.memo(FunctionalComponent[Props] { props =>
			//println(s"CardElement render ${props.card.key}")

			val server = ServerContext.accessState()

			val card = props.card

			val refreshFunction = card.refreshFunction
			val (state, setState) = useState(State(loading = refreshFunction.isDefined))

			def refresh(): Unit = {
				refreshFunction foreach { f =>
					f(server) map {
						case Left(error) => setState(State(error = Some(error.description)))
						case Right(data) => setState(State(data = Some(data)))
					}
					setState(State(loading = true))
				}
			}

			useEffect(() => refresh(), Seq())

			val delegate = new CardContentProvider.Delegate {
				override def addCard(newCard: CardContentProvider, afterThis: Boolean): Unit = props.dispatcher.dispatch(Add(card = newCard, after = if (afterThis) Some(card) else None))
				override def refreshThis(): Unit = refresh()
				override def replaceWithError(error: String): Unit = setState(State(error = Some(error)))
				override def closeThis(): Unit = props.dispatcher.dispatch(Remove(card))
			}

			if (card.managed) {
				Card(
					title = Some(card.title),
					onClose = if (card.canClose) Some(() => props.dispatcher.dispatch(Remove(card))) else None,
					onRefresh = if (refreshFunction.isDefined) Some(() => {
						if (!state.loading) refresh()
					}) else None,
					fullWidth = true,
					dark = false
				)(
					if (state.loading) WaitIcon()
					else state.error match {
						case Some(error) => span(className := "nfw-error")(error)
						case None =>
							card.content(
								d = delegate,
								contentData = state.data
							)
					}
				)
			}
			else {
				card.content(d = delegate, contentData = None)
			}
		}, (prevProps: Props, nextProps: Props) => prevProps.card eq nextProps.card)
	}

	@react object View {
		case class Props
		(
			defaultCard: CardContentProvider
		)

		val component = FunctionalComponent[Props] { props =>
			val state = CircuitState[Model](() => Model(Seq(props.defaultCard)), new AppCircuit(_))

			LayoutTable(gapSize = GapSize.Medium, fullWidth = true)(
				state.value.cards map { card =>
					LTFullRow(align = HoriAlign.Left)(
						CardElement(card = card, dispatcher = state.dispatcher)
					).withKey(key = card.key)
				}
			)
		}
	}
}
